// WaterfallChart.tsx — hand-rolled responsive SVG cost waterfall (no chart library).
// Einzahlungen -> +Kursgewinn -> -TER -> -Gebühren -> -KESt -> Netto. In backtest mode
// the TER bar is greyed/hatched, informational only, excluded from the running total
// (D-007) — see computeWaterfallBars for the arithmetic.
import type { Waterfall } from '@etf-cockpit/engine';
import {
  computeWaterfallBars,
  waterfallExtentCents,
  type WaterfallBar,
  type WaterfallMode,
} from '../lib/waterfall-geometry.js';
import { formatCents, formatCentsCompact } from '../lib/format.js';
import { readCssVar } from '../lib/theme.js';

export interface WaterfallChartProps {
  waterfall: Waterfall;
  mode: WaterfallMode;
}

const VB_WIDTH = 360;
const VB_HEIGHT = 250;
const PLOT_TOP = 26;
const PLOT_BOTTOM = 160;
const GAP = 8;
// The category labels below each bar are rotated -35° anchored at their own right
// edge, which swings the leftmost bar's label ("Einzahlungen", the longest one)
// past x = 0 in viewBox space. SVG's default overflow is hidden, so without this
// headroom that label's first few characters were silently clipped off. Padding
// only the left side keeps the bars themselves at their original scale/position.
const LABEL_PAD_LEFT = 24;

// Palette (see src/styles/global.css): positive bars = --positive, deduction bars =
// --negative, net bar follows the current --accent theme preset (lib/theme.ts),
// informational (TER) bar = --text-muted.
const COLOR_TOTAL_START = '#93a0b8'; // Einzahlungen — informational starting total
const COLOR_POSITIVE = '#4ade80';
const COLOR_NEGATIVE = '#f87171';
const COLOR_INFO_STROKE = '#93a0b8';

function signedLabel(bar: WaterfallBar): string {
  if (bar.kind === 'total') return bar.key === 'contributed' ? 'Einzahlungen' : 'Netto';
  if (bar.kind === 'informational') return 'TER*';
  const prefix = bar.kind === 'positive' ? '+' : '−';
  return `${prefix}${bar.label}`;
}

function barColor(bar: WaterfallBar, accent: string): string {
  if (bar.kind === 'total') return bar.key === 'contributed' ? COLOR_TOTAL_START : accent;
  if (bar.kind === 'positive') return COLOR_POSITIVE;
  if (bar.kind === 'negative') return COLOR_NEGATIVE;
  return 'url(#waterfall-hatch)';
}

export function WaterfallChart({ waterfall, mode }: WaterfallChartProps) {
  const accent = readCssVar('--accent', '#60a5fa');
  const bars = computeWaterfallBars(waterfall, mode);
  const extent = waterfallExtentCents(bars);
  const span = Math.max(1, extent.max - extent.min);
  const plotHeight = PLOT_BOTTOM - PLOT_TOP;

  function yFor(cents: number): number {
    return PLOT_BOTTOM - ((cents - extent.min) / span) * plotHeight;
  }

  const n = bars.length;
  const barAreaWidth = (VB_WIDTH - GAP * (n + 1)) / n;
  const barWidth = Math.min(52, barAreaWidth * 0.72);
  const zeroY = yFor(0);
  const hasInformational = bars.some((b) => b.kind === 'informational');

  return (
    <div>
      <svg
        class="waterfall-svg"
        viewBox={`${-LABEL_PAD_LEFT} 0 ${VB_WIDTH + LABEL_PAD_LEFT} ${VB_HEIGHT}`}
        role="img"
        aria-label={`Kostenwasserfall: Einzahlungen ${formatCents(waterfall.contributedCents)}, Netto ${formatCents(waterfall.netWealthCents)}`}
      >
        <defs>
          <pattern id="waterfall-hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill="rgba(147,160,184,0.12)" />
            <line x1="0" y1="0" x2="0" y2="6" stroke={COLOR_INFO_STROKE} stroke-width="2" />
          </pattern>
        </defs>

        {/* zero baseline */}
        <line x1={0} y1={zeroY} x2={VB_WIDTH} y2={zeroY} stroke="rgba(232,236,244,0.25)" stroke-width="1" />

        {bars.map((bar, i) => {
          const cx = GAP + i * (barAreaWidth + GAP) + barAreaWidth / 2;
          const x = cx - barWidth / 2;
          const yTop = yFor(bar.toCents);
          const yBottom = yFor(bar.fromCents);
          const height = Math.max(1, yBottom - yTop);
          const isDashedOutline = bar.kind === 'informational';
          const labelValue =
            bar.kind === 'total'
              ? formatCentsCompact(bar.toCents)
              : `${bar.deltaCents !== undefined && bar.deltaCents >= 0 ? '+' : '−'}${formatCentsCompact(Math.abs(bar.deltaCents ?? 0))}`;

          return (
            <g key={bar.key}>
              <rect
                x={x}
                y={yTop}
                width={barWidth}
                height={height}
                fill={barColor(bar, accent)}
                stroke={isDashedOutline ? COLOR_INFO_STROKE : 'none'}
                stroke-dasharray={isDashedOutline ? '3,2' : undefined}
                rx={2}
                opacity={isDashedOutline ? 0.7 : 1}
              >
                <title>
                  {bar.label}: {formatCents(bar.kind === 'total' ? bar.toCents : (bar.deltaCents ?? 0))}
                </title>
              </rect>
              <text x={cx} y={yTop - 6} text-anchor="middle" font-size="9.5" fill="#e8ecf4">
                {labelValue}
              </text>
              <text
                x={cx}
                y={PLOT_BOTTOM + 14}
                text-anchor="end"
                font-size="9.5"
                fill="#93a0b8"
                transform={`rotate(-35 ${cx} ${PLOT_BOTTOM + 14})`}
              >
                {signedLabel(bar)}
              </text>
            </g>
          );
        })}
      </svg>
      {hasInformational ? (
        <p class="field-hint">
          * TER bereits im Kurs enthalten — geschätzter, informativer Wert, nicht im
          Saldo (Netto) enthalten.
        </p>
      ) : null}
    </div>
  );
}
